import { useEffect, useState } from 'react';
import { api, getAuth, loadWithRetry, peek, setAuth } from './api';
import GalleryPage, { UNCATEGORIZED } from './GalleryPage';
import ArtworkPage from './ArtworkPage';
import LoginPage from './LoginPage';
import KaiPage from './KaiPage';
import { Toaster, toast } from './feedback';
import { SITE_NAME, toAppPath, toBrowserPath } from './config';
import SearchBar from './SearchBar';
import './App.css';

// Điều hướng bằng URL (/, /danh-muc/:id, /tranh/:id, /dang-nhap, /qr/:token, /kai, /kai/album/:id) để nút Back của trình duyệt hoạt động
function usePath() {
  // Lưu cả phần ?… (vd /kai/album/3?anh=12 để mở thẳng một ảnh) nhưng các trang chỉ so khớp theo đường dẫn
  const current = () => toAppPath(window.location.pathname) + window.location.search;
  const [location, setLocation] = useState(current);

  useEffect(() => {
    const onPop = () => setLocation(current());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = (to) => {
    if (to !== current()) {
      window.history.pushState(null, '', toBrowserPath(to));
      setLocation(to);
    }
    window.scrollTo(0, 0);
  };

  return [location.split('?')[0], navigate, location];
}

export default function App() {
  const [path, navigate, location] = usePath();
  const [auth, setAuthState] = useState(getAuth());
  const isAdmin = auth?.role === 'ADMIN';
  const artworkId = path.match(/^\/tranh\/(\d+)$/)?.[1];
  const qrToken = path.match(/^\/qr\/([A-Za-z0-9]+)$/)?.[1];
  // Trang của một danh mục: /danh-muc/12, hoặc /danh-muc/chua-phan-loai cho tranh không có danh mục
  const folderParam = path.match(/^\/danh-muc\/(\d+|chua-phan-loai)$/)?.[1];
  const folder = folderParam === undefined ? undefined : folderParam === 'chua-phan-loai' ? UNCATEGORIZED : Number(folderParam);
  const folderPath = (key) => (key == null ? '/' : `/danh-muc/${key === UNCATEGORIZED ? 'chua-phan-loai' : key}`);

  // Dữ liệu chung của trang admin: danh mục (kèm số tranh), số liệu thống kê, chất liệu đã dùng.
  // Không tải toàn bộ tranh: trang danh mục tự tải tranh của đúng danh mục đó (đủ nhanh dù có hàng nghìn tranh).
  const [categories, setCategories] = useState(null);
  const [stats, setStats] = useState(null); // { total, withQr, trash, uncategorized }
  const [materials, setMaterials] = useState([]);
  const [homeError, setHomeError] = useState('');
  const [waking, setWaking] = useState(false); // máy chủ đang khởi động lại, đang tự thử lại
  const [query, setQuery] = useState('');
  const [quickQuery, setQuickQuery] = useState(''); // ô tìm nhanh trên đầu trang
  const [quickResults, setQuickResults] = useState([]);

  const loadHome = () => {
    // Lần đầu mở trang: hiện ngay bản đã lưu (nếu có), bản mới từ máy chủ thay vào sau
    if (!categories) {
      const cats = peek(() => api.getCategories());
      const st = peek(() => api.getStats());
      if (cats && st) {
        setCategories(cats);
        setStats(st);
        setMaterials(peek(() => api.getMaterials()) || []);
      }
    }
    return loadWithRetry(() => Promise.all([api.getCategories(), api.getStats(), api.getMaterials()]), () => setWaking(true))
      .then(([cats, st, mats]) => {
        setCategories(cats);
        setStats(st);
        setMaterials(mats);
        setHomeError('');
      })
      .catch(() => setHomeError('Không kết nối được máy chủ. Vui lòng tải lại trang sau ít phút.'))
      .finally(() => setWaking(false));
  };

  // Chỉ admin xem được; tải lại mỗi khi vào trang chủ / trang danh mục để thấy ngay thay đổi vừa làm
  useEffect(() => {
    if (!isAdmin || (path !== '/' && folder === undefined && categories)) return;
    loadHome();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, isAdmin]);

  // Ô tìm nhanh: hỏi máy chủ sau khi ngừng gõ một chút
  useEffect(() => {
    const q = quickQuery.trim();
    if (!q || !isAdmin) {
      setQuickResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api.searchArtworks(q).then((list) => !cancelled && setQuickResults(list)).catch(() => {});
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [quickQuery, isAdmin]);

  // Phiên đăng nhập hết hạn (sau 24 giờ) thì báo cho admin biết, không đăng xuất lặng lẽ
  useEffect(() => {
    const onExpired = () => {
      if (!toAppPath(window.location.pathname).startsWith('/kai')) toast('Phiên đăng nhập đã hết, vui lòng đăng nhập lại');
    };
    window.addEventListener('session-expired', onExpired);
    return () => window.removeEventListener('session-expired', onExpired);
  }, []);

  useEffect(() => {
    const sync = () => setAuthState(getAuth());
    window.addEventListener('auth-change', sync);
    return () => window.removeEventListener('auth-change', sync);
  }, []);

  // Hết phiên hoặc đăng xuất khi đang ở trang chỉ dành cho admin thì quay về trang chủ
  useEffect(() => {
    if (artworkId && !isAdmin) navigate('/');
    if (path === '/quan-ly') navigate('/'); // trang Quản lý cũ đã gộp vào trang chủ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, isAdmin]);

  const logout = () => {
    setAuth(null);
    toast('Đã đăng xuất');
    navigate('/');
  };

  // Trang /kai: thư viện ảnh/video cho khách, giao diện tách hẳn khỏi kho chứng nhận
  if (path === '/kai' || path.startsWith('/kai/'))
    return (
      <KaiPage
        path={path}
        location={location}
        navigate={navigate}
        isAdmin={isAdmin}
        onLogout={() => setAuth(null)} // trang Kai tự báo "đã đăng xuất" theo ngôn ngữ đang chọn
      />
    );

  // Người quét QR: chỉ thấy đúng một tranh, không có thanh trên cùng hay nút điều hướng nào
  if (qrToken) {
    return (
      <div className="app qr-page">
        <img className="qr-brand" src="/brand.png" alt={SITE_NAME} />
        <ArtworkPage qrToken={qrToken} />
      </div>
    );
  }

  let content;
  if (!isAdmin && path !== '/dang-nhap') content = null; // người ngoài chỉ thấy tên web và nút Đăng nhập
  else if (artworkId)
    content = (
      <ArtworkPage
        key={artworkId}
        id={Number(artworkId)}
        categories={categories || []}
        materials={materials}
        // Quay lại / sau khi xoá: về trang danh mục của tranh
        onBack={(artwork) => navigate(folderPath(artwork ? artwork.categoryId ?? UNCATEGORIZED : null))}
        onDeleted={(artwork) => {
          navigate(folderPath(artwork.categoryId ?? UNCATEGORIZED));
          loadHome();
        }}
      />
    );
  else if (path === '/dang-nhap') content = <LoginPage onSuccess={() => navigate('/')} />;
  else
    content = (
      <GalleryPage
        key={folderParam ?? 'home'} // mỗi trang danh mục bắt đầu với ô tìm kiếm / chế độ chọn trống
        categories={categories}
        stats={stats}
        materials={materials}
        waking={waking}
        folder={folder}
        onOpenFolder={(key) => {
          setQuery('');
          navigate(folderPath(key));
        }}
        onChanged={loadHome}
        setCategories={setCategories} // sửa xong cập nhật ngay danh sách danh mục, không chờ tải lại
        error={homeError}
        query={query}
        onQueryChange={setQuery}
        onOpen={(artwork) => navigate(`/tranh/${artwork.id}`)}
      />
    );

  return (
    <div className="app">
      <header className="header">
        <button className="site-name" onClick={() => navigate('/')} aria-label={SITE_NAME}>
          <img src="/brand.png" alt={SITE_NAME} />
        </button>
        {isAdmin && (
          <div className="header-search">
            <SearchBar
              items={quickResults}
              matches={() => true} // máy chủ đã lọc sẵn
              describe={(a) => ({
                key: a.id,
                title: a.code,
                sub: [a.category || 'Chưa phân loại', a.ownerName].filter(Boolean).join(' · '),
              })}
              query={quickQuery}
              onQueryChange={setQuickQuery}
              onPick={(a) => {
                setQuickQuery('');
                navigate(`/tranh/${a.id}`);
              }}
              placeholder="Tìm nhanh mã chứng nhận, chủ sở hữu…"
              emptyText="Không tìm thấy tranh phù hợp"
            />
          </div>
        )}
        <div className="account">
          {auth ? (
            <>
              <span>{auth.fullName || auth.username}</span>
              <button onClick={logout}>Đăng xuất</button>
            </>
          ) : path === '/dang-nhap' ? (
            <button onClick={() => navigate('/')}>Quay lại</button>
          ) : (
            <button onClick={() => navigate('/dang-nhap')}>Đăng nhập</button>
          )}
        </div>
      </header>

      <main>{content}</main>
      <Toaster />
    </div>
  );
}
